import {
  Feature,
  OrderStatus as OrderStatusEnum,
  StructureStatus,
} from "@enums";
import { AutoFixHigh, ChevronLeft } from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  OrderStatus,
  useAutomationIntegrationApiV1ListMachinesQuery,
  useAutomationIntegrationApiV1ListTemplateDetailsQuery,
  useAutomationIntegrationApiV1ListTemplatesQuery,
  useAutomationIntegrationApiV1RequestPlanGenerationMutation,
  useWebApiV1GetUserFeatureSwitchesQuery,
  useWebApiV1OrderQuery,
  useWebApiV1RelatedPrescriptionsQuery,
  useWebApiV1UpdateOrderMutation,
} from "@providers/hop-ord-server/api";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import StructureMapping, {
  StructureMap,
} from "@pages/OrderTracker/components/PlanningAutomation/StructureMapping";

import { useAppDispatch } from "@hooks";
import BottomActionBar from "@pages/Layout/BottomActionBar";
import useRefreshImportedData from "@utils/useRefreshImportedData";
import { setProgressionActive } from "../../../../features/settingsSlice";

interface Props {
  orderId: number;
}

interface StructureObj {
  name: string;
  type: string;
}

const PlanningTemplateApi = ({ orderId }: Props): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [structureState, setStructureState] = useState<StructureMap[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [relatedPrescription, setRelatedPrescription] = useState("");
  const [relatedPrescriptionId, setRelatedPrescriptionId] = useState("");
  const [selectedMachine, setSelectedMachine] = useState("");
  const [selectedBeamEnergy, setSelectedBeamEnergy] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showBeamEnergyError, setShowBeamEnergyError] = useState(false);
  const theme = useTheme();

  // Custom styles for reduced height inputs and closer labels
  const inputStyles = {
    "& .MuiInputBase-root": {
      height: "42.5px", // Reduced from ~50px (15% reduction)
    },
    "& .MuiFormLabel-root": {
      transform: "translate(14px, 12px) scale(1)", // Closer to input
      "&.Mui-focused, &.MuiFormLabel-filled": {
        transform: "translate(14px, -6px) scale(0.75)", // Adjusted for closer positioning
      },
    },
    "& .MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.75)",
    },
  };

  const selectStyles = {
    height: "42.5px", // Reduced from ~50px (15% reduction)
    "& .MuiSelect-select": {
      paddingTop: "10px",
      paddingBottom: "10px",
    },
  };

  const formControlStyles = {
    "& .MuiInputLabel-root": {
      transform: "translate(14px, 12px) scale(1)", // Closer to input
      "&.Mui-focused, &.MuiInputLabel-shrink": {
        transform: "translate(14px, -6px) scale(0.75)",
      },
    },
  };

  const {
    handleRefresh: handleRefreshImportedData,
    refreshStatus,
    structuresRetrieved,
    setRefreshStatus,
  } = useRefreshImportedData(orderId, selectedTemplate);

  const [updateOrder] = useWebApiV1UpdateOrderMutation();

  const { data: featureSwitchData } = useWebApiV1GetUserFeatureSwitchesQuery();
  const showPlanReview = featureSwitchData?.find(
    (feature) => feature.name === Feature.PLAN_REVIEW,
  );

  /** List Data query */
  const { data: machines, isLoading: machinesLoading } =
    useAutomationIntegrationApiV1ListMachinesQuery({
      orderId,
    });

  useEffect(() => {
    // Default to the first machine if there is only one
    if (machines?.length === 1 && !selectedMachine) {
      setSelectedMachine(machines[0].name);
    }
  }, [machines]);

  // Get beam energies for selected machine
  const beamEnergyOptions =
    machines?.find((m) => m.name === selectedMachine)?.beam_energies || [];

  // Auto-select beam energy if only one option is available
  useEffect(() => {
    if (beamEnergyOptions.length === 1 && !selectedBeamEnergy) {
      // Use setTimeout to prevent scroll behavior during auto-selection
      setTimeout(() => {
        setSelectedBeamEnergy(beamEnergyOptions[0]);
      }, 0);
    }
  }, [beamEnergyOptions, selectedBeamEnergy]);

  // Temp options while the list data is not available
  const machineOptions = machines?.map((data) => data.name) || [];

  const { data: order } = useWebApiV1OrderQuery({
    orderId,
  });

  // Site query (to get names and ID's for related prescriptions)
  const { data: relatedPrescriptions } = useWebApiV1RelatedPrescriptionsQuery({
    orderId: orderId.toString(),
  });

  const rtStructExists = order?.rtstructStatus === StructureStatus.COMPLETE;
  const ctExists = order?.ctStatus === StructureStatus.COMPLETE;

  const showAutomation = rtStructExists && ctExists;

  const warningText =
    !ctExists && !rtStructExists
      ? "Missing CT and RT structure set."
      : !ctExists
        ? "Missing CT."
        : !rtStructExists
          ? "Missing RT structure set."
          : "";

  const { data: templateNames, isLoading: templateNamesLoading } =
    useAutomationIntegrationApiV1ListTemplatesQuery({ orderId });

  const { data: template } =
    useAutomationIntegrationApiV1ListTemplateDetailsQuery(
      selectedTemplate
        ? { orderId, templateName: selectedTemplate }
        : skipToken,
    );

  const [sendPlan] =
    useAutomationIntegrationApiV1RequestPlanGenerationMutation();

  const setOrderStatus = (status: OrderStatus): Promise<unknown> => {
    return updateOrder({
      orderId,
      orderIn: {
        status,
      },
    });
  };

  const relatedPrescriptionOptions = relatedPrescriptions?.map(
    (prescription) => ({
      label: prescription.relatedPrescriptionName,
      id: prescription.relatedPrescriptionId,
    }),
  );

  const templateOptions = [
    "",
    ...(templateNames?.map(
      (templateName: { name: string }) => templateName.name,
    ) || []),
  ];

  useEffect(() => {
    if (relatedPrescriptions?.length === 1 && !relatedPrescription) {
      // Automatically select the first related prescription and id if there is only one
      setRelatedPrescription(relatedPrescriptions[0].relatedPrescriptionName);
      setRelatedPrescriptionId(
        relatedPrescriptions[0].relatedPrescriptionId.toString(),
      );
    }
  }, [relatedPrescriptions]);

  // Auto-select "Prostate + LN" template if available and no template is selected
  useEffect(() => {
    if (templateNames && !selectedTemplate) {
      const prostateTemplate = templateNames.find(
        (template: { name: string }) => template.name === "Prostate + LN",
      );
      if (prostateTemplate) {
        setSelectedTemplate("Prostate + LN");
        setRefreshStatus("refetching order");
      }
    }
  }, [templateNames, selectedTemplate]);

  const scrollToFirstInvalidField = (structures: StructureMap[]) => {
    if (!selectedMachine) {
      const machineElement = document.getElementById("selected-machine");
      machineElement?.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    } else {
      const firstInvalidStructure = structures.find((s) => !s.isValid);
      const firstInvalidElement = document.getElementById(
        `planning-template-value-${firstInvalidStructure?.id}`,
      );
      firstInvalidElement?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  };

  const runValidation = (structures: StructureMap[]) => {
    const allValid = structures.every((s) => {
      return !(s.mandatory && !s.values.length);
    });

    const updatedStructures = structures.map((structure) => {
      const isInvalid = structure.mandatory && !structure.values.length;
      return {
        ...structure,
        isValid: !isInvalid,
      };
    });

    setStructureState(updatedStructures);
    return (
      allValid &&
      !!selectedMachine &&
      !!selectedBeamEnergy &&
      !!selectedTemplate &&
      !!relatedPrescription
    );
  };

  const submitPlanGeneration = async () => {
    setHasSubmitted(true);
    setShowBeamEnergyError(!!selectedMachine);
    if (!runValidation(structureState)) {
      scrollToFirstInvalidField(structureState);
      return;
    }

    // Set progression active when Generate Plan button is pressed
    dispatch(setProgressionActive(true));

    // Submit has been clicked and / or validation has run successfully. Submit form.
    const structureMap: StructureObj[] = [];
    structureState.forEach((s) => {
      if (s.values) {
        const map = s.values.map((value) => ({
          name: s.name,
          type: value,
        }));
        structureMap.push(...map);
      }
    });

    // Check if demo mode is enabled
    const IS_DEMO = true;

    if (IS_DEMO) {
      // Skip API call in demo mode, but still update status
      setHasSubmitted(true);
      setOrderStatus("sent_to_automation");
      navigate("automated");
    } else {
      // Normal behavior - make API call
      sendPlan({
        orderId,
        planGenerationAttemptIn: {
          template: selectedTemplate,
          structures: structureMap,
          machine: selectedMachine,
          beam_energy: selectedBeamEnergy,
          related_prescription: relatedPrescription,
          related_prescription_id: relatedPrescriptionId,
        },
      }).then(() => {
        setHasSubmitted(true);
        setOrderStatus("sent_to_automation");
        navigate("automated");
      });
    }
  };

  const showCancelButton = order?.status === OrderStatusEnum.SENT_TO_AUTOMATION;

  const BottomBar = () => {
    return (
      <BottomActionBar>
        <Stack
          direction="row-reverse"
          width={1}
          justifyContent="space-between"
          padding={2}
          borderTop={`1px solid ${theme.palette.divider}`}
        >
          <Button
            data-testid={"planning-template-submit"}
            size="medium"
            variant="contained"
            color="primary"
            disabled={order?.status !== OrderStatusEnum.SENT_TO_AUTOMATION}
            onClick={() => {
              if (showPlanReview) {
                navigate(`/commandcenter/${orderId}/plan-review`);
              } else {
                navigate(`/commandcenter/${orderId}/handover`);
              }
            }}
            sx={{
              opacity:
                order?.status !== OrderStatusEnum.SENT_TO_AUTOMATION ? 0.6 : 1,
              cursor:
                order?.status !== OrderStatusEnum.SENT_TO_AUTOMATION
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            Next: {showPlanReview ? "Plan Review" : "Handover"}
          </Button>
          {showCancelButton && (
            <Button
              data-testid={"planning-template-submit"}
              size="medium"
              variant="outlined"
              color="primary"
              onClick={() => navigate(`/commandcenter/${orderId}/automated`)}
            >
              Cancel
            </Button>
          )}
        </Stack>
      </BottomActionBar>
    );
  };

  const refreshingImportedData = refreshStatus !== "idle";

  // Listen for refresh event from parent component
  useEffect(() => {
    const handleRefreshEvent = () => {
      window.dispatchEvent(new CustomEvent("refreshStructuresStart"));
      handleRefreshImportedData();
    };

    window.addEventListener("refreshStructures", handleRefreshEvent);
    return () => {
      window.removeEventListener("refreshStructures", handleRefreshEvent);
    };
  }, [handleRefreshImportedData]);

  // Dispatch end event when refresh status changes
  useEffect(() => {
    if (refreshStatus === "idle") {
      window.dispatchEvent(new CustomEvent("refreshStructuresEnd"));
    }
  }, [refreshStatus]);

  if (templateNamesLoading || machinesLoading) {
    return <BottomBar />;
  }

  return (
    <Stack width={1} height={1} marginBottom={3} gap={2}>
      {!showAutomation && (
        <Stack gap={2}>
          <Typography>
            Unable to start planning automation. {warningText}
          </Typography>
          <Button
            variant="outlined"
            sx={{ width: "max-content" }}
            startIcon={<ChevronLeft />}
            onClick={() => navigate("/commandcenter")}
          >
            Back to command center
          </Button>
        </Stack>
      )}
      {showAutomation && (
        <Stack gap={3} width={1}>
          <Stack gap={1} width="60%">
            <FormControl error={hasSubmitted && !relatedPrescription}>
              <Autocomplete
                disableClearable
                options={relatedPrescriptionOptions || []}
                value={{
                  label: relatedPrescription,
                  id: relatedPrescriptionId,
                }}
                fullWidth
                onChange={(
                  _,
                  newValue: { label: string; id: string } | null,
                ) => {
                  setRelatedPrescription(newValue?.label || "");
                  setRelatedPrescriptionId(newValue?.id || "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={hasSubmitted && !relatedPrescription}
                    label="Related prescription *"
                    data-testid="related-prescription-select"
                    sx={inputStyles}
                  />
                )}
              />
              {hasSubmitted && !relatedPrescription && (
                <FormHelperText>This field is required</FormHelperText>
              )}
            </FormControl>
          </Stack>

          <Stack gap={1} width="60%">
            <Typography variant="subtitle1" sx={{ mb: 0.25 }}>
              Automation Model
            </Typography>
            <FormControl error={hasSubmitted && !selectedTemplate}>
              <Autocomplete
                options={templateOptions || []}
                value={selectedTemplate}
                fullWidth
                onChange={(_, newValue: string | null) => {
                  setSelectedTemplate(newValue || "");
                  if (refreshStatus === "idle" && newValue) {
                    setRefreshStatus("refetching order");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={hasSubmitted && !selectedTemplate}
                    label="Automated template *"
                    data-testid="planning-template-select"
                    sx={inputStyles}
                  />
                )}
              />
              {hasSubmitted && !selectedTemplate && (
                <FormHelperText>This field is required</FormHelperText>
              )}
            </FormControl>
          </Stack>

          {selectedTemplate && (
            <StructureMapping
              template={template}
              availableStructures={
                refreshingImportedData ? null : structuresRetrieved.structures
              }
              hasSubmitted={hasSubmitted}
              setHasSubmitted={setHasSubmitted}
              setStructureState={setStructureState}
              structureState={structureState}
              runValidation={runValidation}
            />
          )}

          <Stack gap={1} width="60%">
            <Typography variant="subtitle1" sx={{ mb: 0.25 }}>
              Machine
            </Typography>
            <FormControl error={hasSubmitted && !selectedMachine}>
              <Autocomplete
                options={machineOptions || []}
                value={selectedMachine}
                fullWidth
                onChange={(_, newValue: string | null) => {
                  setSelectedMachine(newValue || "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={hasSubmitted && !selectedMachine}
                    label="Machine *"
                    data-testid="selected-machine"
                    sx={inputStyles}
                  />
                )}
              />
              {hasSubmitted && !selectedMachine && (
                <FormHelperText>This field is required</FormHelperText>
              )}
            </FormControl>
          </Stack>

          <Stack gap={1} width="60%">
            <Typography variant="subtitle1" sx={{ mb: 0.25 }}>
              Energy
            </Typography>
            <FormControl
              error={hasSubmitted && showBeamEnergyError && !selectedBeamEnergy}
              disabled={!selectedMachine}
              sx={formControlStyles}
            >
              <InputLabel id="selected-beam-energy-label">Energy *</InputLabel>
              <Tooltip
                title={
                  !selectedMachine
                    ? "Select a machine first to view the available energy options"
                    : ""
                }
                placement="top"
              >
                <Select
                  id="selected-beam-energy"
                  data-testid="selected-beam-energy"
                  value={selectedBeamEnergy}
                  labelId="selected-beam-energy"
                  label="Energy *"
                  onChange={(e) => setSelectedBeamEnergy(e.target.value)}
                  sx={selectStyles}
                  autoFocus={false}
                  MenuProps={{
                    disableScrollLock: true,
                    disableAutoFocus: true,
                    disableRestoreFocus: true,
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                >
                  {beamEnergyOptions.map((energy) => (
                    <MenuItem key={energy} value={energy}>
                      {energy}
                    </MenuItem>
                  ))}
                </Select>
              </Tooltip>
              {hasSubmitted && showBeamEnergyError && !selectedBeamEnergy && (
                <FormHelperText>This field is required</FormHelperText>
              )}
            </FormControl>
          </Stack>

          <Button
            startIcon={<AutoFixHigh />}
            sx={{ width: "max-content" }}
            variant="contained"
            color="primary"
            onClick={() => submitPlanGeneration()}
          >
            Generate Plan
          </Button>
          {(!templateNames?.length || !machineOptions?.length) && (
            <Stack direction="row" gap={1}>
              <Typography color="error">
                No templates and/or machines found.
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
      <BottomBar />
    </Stack>
  );
};

export default PlanningTemplateApi;
